import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Checkbox, FormControlLabel, Button
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import axios from 'axios';
import ValidateOrderDialog from './ValidateOrderDialog';

import { useAuth } from '../../contexts/AuthContext';
import { useNotify } from '../../contexts/FeedbackContext';

const OrderSidebar = ({
    order, items,
    orderType,
    onValidate,
    deliveryDate, setDeliveryDate,
    specialProduction, setSpecialProduction
}) => {
    const { user } = useAuth();
    const notify = useNotify();
    const [users, setUsers] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    // El cliente mayorista confirma sus propios pedidos; el resto firma con usuario + contraseña
    const needsCredentials = !user?.is_wholesale_client;

    useEffect(() => {
        const fetchUsers = async () => {
            if (user?.is_wholesale_client) return;
            try {
                const response = await axios.get('/api/users/?is_active=true');
                setUsers(response.data.results || response.data);
            } catch (error) {
                console.error('Error fetching users:', error);
                notify('No se pudo cargar la lista de vendedores.', 'warning');
            }
        };
        fetchUsers();
    }, [user?.is_wholesale_client, notify]);

    const calculateTotal = () => {
        return items.reduce((sum, item) => sum + parseFloat(item.subtotal || 0), 0);
    };

    const calculateTotalWeight = () => {
        return items.reduce((sum, item) => {
            const quantity = parseFloat(item.quantity || 0);
            const product = item.product || {};

            if (product.unit === 'kg') {
                return sum + quantity;
            }
            const weightPerUnit = parseFloat(product.weight_per_unit || 0);
            return sum + (quantity * weightPerUnit);
        }, 0);
    };

    const handleValidate = async () => {
        if (needsCredentials) {
            setDialogOpen(true);
            return;
        }
        try {
            await onValidate(null, null);
        } catch (err) {
            // El error ya es manejado y expuesto en el estado del hook useOrder
            console.error('Validation failed:', err);
        }
    };

    // Date logic helpers could be moved to utils
    const getMinDate = () => {
        if (!specialProduction) return undefined;
        const minDate = new Date();
        minDate.setDate(minDate.getDate() + 7);
        // Fecha en hora local: toISOString() usa UTC y después de las 21 h corría el mínimo un día
        const pad = (n) => String(n).padStart(2, '0');
        return `${minDate.getFullYear()}-${pad(minDate.getMonth() + 1)}-${pad(minDate.getDate())}`;
    };

    return (
        <Box sx={{ p: 2, bgcolor: '#f6f7f8', height: '100%', borderLeft: '1px solid #e7edf3', display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* Totals */}
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="textSecondary">Cantidad Total (kg)</Typography>
                    <Typography fontWeight="500">{calculateTotalWeight().toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="textSecondary">Items</Typography>
                    <Typography fontWeight="500">{items.length}</Typography>
                </Box>
                <Box sx={{ borderTop: '1px solid #e7edf3', mt: 1, pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography fontWeight="700">Total</Typography>
                    <Typography variant="h5" fontWeight="700" color="primary">
                        ${calculateTotal().toLocaleString('es-AR', { minimumFractionDigits: 0 })}
                    </Typography>
                </Box>
            </Box>

            {/* Delivery Info */}
            <Box sx={{ borderTop: '1px solid #e7edf3', pt: 1 }}>
                <FormControlLabel
                    sx={{ my: -0.5 }}
                    control={
                        <Checkbox
                            size="small"
                            checked={specialProduction}
                            onChange={(e) => {
                                setSpecialProduction(e.target.checked);
                                if (!e.target.checked && deliveryDate) setDeliveryDate(null);
                            }}
                        />
                    }
                    label={<Typography variant="body2">Producción Especial (7 días anticipación)</Typography>}
                />
                {specialProduction && (
                    <TextField
                        fullWidth
                        type="date"
                        label="Fecha de Entrega"
                        value={deliveryDate || ''}
                        onChange={(e) => setDeliveryDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: getMinDate() }}
                        size="small"
                        sx={{ mt: 1 }}
                    />
                )}
            </Box>

            {/* Validation */}
            <Box sx={{ borderTop: '1px solid #e7edf3', pt: 1.5 }}>
                <Button
                    fullWidth
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleValidate}
                    disabled={items.length === 0 || (!order?.client && !user?.is_wholesale_client)}
                    sx={{ py: 1.5, fontWeight: 'bold' }}
                >
                    {user?.is_wholesale_client ? 'Confirmar y Revisar Pedido' : 'Cargar Pedido'}
                </Button>
            </Box>

            {needsCredentials && (
                <ValidateOrderDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onConfirm={onValidate}
                    users={users}
                    currentUser={user}
                    summary={{
                        clientName: order?.client?.name,
                        itemsCount: items.length,
                        total: calculateTotal(),
                    }}
                />
            )}
        </Box>
    );
};

export default OrderSidebar;
